import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { API_BASE } from "../config/api";

const REGIONES_CHAMPIONS = [
    { Region_ID: 1, Region_Nombre: "México" },
    { Region_ID: 2, Region_Nombre: "Latinoamérica" },
];

const GRADOS_POR_REGION = {
    1: [
        { Grado_ID: 3, Grado_Nombre: "Especialista" },
        { Grado_ID: 4, Grado_Nombre: "Elite" },
        { Grado_ID: 6, Grado_Nombre: "Product Manager" },
    ],
    2: [
        { Grado_ID: 4, Grado_Nombre: "Elite" },
        { Grado_ID: 5, Grado_Nombre: "Líder" },
    ],
};

const DEFAULT_FORM = {
    regionId: "",
    desafioId: "",
    distyIds: [],
    gradoIds: [],
    fechaInicio: "",
    fechaFin: "",
    activo: 1,
};

function ChampionsAsignacion() {
    const [desafios, setDesafios] = useState([]);
    const [loadingDesafios, setLoadingDesafios] = useState(false);

    const [asignaciones, setAsignaciones] = useState([]);
    const [loadingAsignaciones, setLoadingAsignaciones] = useState(false);

    const [distys, setDistys] = useState([]);
    const [loadingDistys, setLoadingDistys] = useState(false);

    const [desafioFiltro, setDesafioFiltro] = useState("");
    const [estadoFiltro, setEstadoFiltro] = useState("");
    const [searchDisty, setSearchDisty] = useState("");

    const [form, setForm] = useState(DEFAULT_FORM);
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const gradosDisponibles = useMemo(() => {
        return GRADOS_POR_REGION[Number(form.regionId)] || [];
    }, [form.regionId]);

    const allDistysSelected = useMemo(() => {
        if (!distys.length) return false;

        return distys.every((disty) =>
            form.distyIds.includes(Number(disty.Disty_ID))
        );
    }, [distys, form.distyIds]);

    const allGradesSelected = useMemo(() => {
        if (!gradosDisponibles.length) return false;

        return gradosDisponibles.every((grado) =>
            form.gradoIds.includes(Number(grado.Grado_ID))
        );
    }, [gradosDisponibles, form.gradoIds]);

    const parseItems = (data) => {
        if (Array.isArray(data?.data?.items)) return data.data.items;
        if (Array.isArray(data?.data?.desafios)) return data.data.desafios;
        if (Array.isArray(data?.data?.rows)) return data.data.rows;
        if (Array.isArray(data?.data)) return data.data;
        if (Array.isArray(data?.items)) return data.items;
        if (Array.isArray(data)) return data;

        return [];
    };

    const normalizeDesafio = (item) => ({
        Desafio_ID: item.Desafio_ID ?? item.desafioId,
        Desafio_Titulo: item.Desafio_Titulo ?? item.titulo,
        Desafio_Producto_Titulo: item.Desafio_Producto_Titulo ?? item.productoTitulo,
        Desafio_Activo: item.Desafio_Activo ?? item.activo,
    });

    const toMysqlDateTime = (value) => {
        if (!value) return null;

        const clean = String(value).trim();

        if (clean.includes("T")) {
            const replaced = clean.replace("T", " ");
            return replaced.length === 16 ? `${replaced}:00` : replaced.substring(0, 19);
        }

        return clean;
    };

    const toDatetimeLocalValue = (value) => {
        if (!value) return "";

        const clean = String(value).trim();

        if (clean.includes("T") && clean.includes("Z")) {
            const date = new Date(clean);

            const formatter = new Intl.DateTimeFormat("sv-SE", {
                timeZone: "America/Mexico_City",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            });

            return formatter.format(date).replace(" ", "T");
        }

        return clean.replace(" ", "T").slice(0, 16);
    };

    const formatDateTimeMx = (value) => {
        if (!value) return "Sin fecha";

        const clean = String(value).trim();

        if (clean.includes("T") && clean.includes("Z")) {
            const date = new Date(clean);

            const formatter = new Intl.DateTimeFormat("es-MX", {
                timeZone: "America/Mexico_City",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });

            return formatter.format(date);
        }

        return clean;
    };

    const fetchDesafios = async () => {
        try {
            setLoadingDesafios(true);

            const res = await fetch(
                `${API_BASE}/api/admin/champions/desafios?limit=100`,
                {
                    credentials: "include",
                }
            );

            const data = await res.json();

            if (!res.ok || data.success === false || data.ok === false) {
                throw new Error(data.message || "No se pudieron cargar los desafíos.");
            }

            const items = parseItems(data)
                .map(normalizeDesafio)
                .filter((item) => item.Desafio_ID && item.Desafio_Titulo);

            setDesafios(items);
        } catch (error) {
            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message || "No se pudieron cargar los desafíos.",
            });
        } finally {
            setLoadingDesafios(false);
        }
    };

    const fetchAsignaciones = async (desafioId = desafioFiltro, activo = estadoFiltro) => {
        if (!desafioId) {
            setAsignaciones([]);
            return;
        }

        try {
            setLoadingAsignaciones(true);

            const params = new URLSearchParams();
            params.set("limit", "100");

            if (activo !== "") {
                params.set("activo", activo);
            }

            const res = await fetch(
                `${API_BASE}/api/admin/champions/desafios/${desafioId}/asignaciones?${params.toString()}`,
                {
                    credentials: "include",
                }
            );

            const data = await res.json();

            if (!res.ok || data.success === false) {
                throw new Error(data.message || "No se pudieron cargar las asignaciones.");
            }

            setAsignaciones(parseItems(data));
        } catch (error) {
            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message || "No se pudieron cargar las asignaciones.",
            });
        } finally {
            setLoadingAsignaciones(false);
        }
    };

    const fetchDistysByRegion = async (regionId, search = "") => {
        if (!regionId) {
            setDistys([]);
            return;
        }

        try {
            setLoadingDistys(true);

            const params = new URLSearchParams();
            params.set("regionId", regionId);

            if (search.trim()) {
                params.set("search", search.trim());
            }

            const res = await fetch(
                `${API_BASE}/api/admin/champions/asignaciones/distys?${params.toString()}`,
                {
                    credentials: "include",
                }
            );

            const data = await res.json();

            if (!res.ok || data.success === false) {
                throw new Error(data.message || "No se pudieron cargar los distys.");
            }

            setDistys(parseItems(data));
        } catch (error) {
            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message || "No se pudieron cargar los distys.",
            });
        } finally {
            setLoadingDistys(false);
        }
    };

    useEffect(() => {
        fetchDesafios();
    }, []);

    const resetForm = () => {
        setForm(DEFAULT_FORM);
        setStep(1);
        setDistys([]);
        setSearchDisty("");
    };

    const openModal = () => {
        resetForm();

        const modalEl = document.getElementById("addAsigDesafioModal");

        if (window.bootstrap?.Modal && modalEl) {
            const modal =
                window.bootstrap.Modal.getInstance(modalEl) ||
                new window.bootstrap.Modal(modalEl);

            modal.show();
        }
    };

    const closeModal = () => {
        const modalEl = document.getElementById("addAsigDesafioModal");

        if (window.bootstrap?.Modal && modalEl) {
            const modal = window.bootstrap.Modal.getInstance(modalEl);
            modal?.hide();
        }

        resetForm();
    };

    const handleRegionChange = async (value) => {
        const regionId = value ? Number(value) : "";

        setForm({
            ...DEFAULT_FORM,
            regionId,
            gradoIds: regionId === 2 ? [4, 5] : [],
        });

        setDistys([]);
        setSearchDisty("");

        if (regionId) {
            await fetchDistysByRegion(regionId);
        }
    };

    const toggleDisty = (distyId) => {
        const cleanId = Number(distyId);

        setForm((prev) => {
            const exists = prev.distyIds.includes(cleanId);

            return {
                ...prev,
                distyIds: exists
                    ? prev.distyIds.filter((id) => id !== cleanId)
                    : [...prev.distyIds, cleanId],
            };
        });
    };

    const toggleAllDistys = () => {
        setForm((prev) => ({
            ...prev,
            distyIds: allDistysSelected
                ? []
                : distys.map((disty) => Number(disty.Disty_ID)),
        }));
    };

    const toggleGrade = (gradoId) => {
        const cleanId = Number(gradoId);

        setForm((prev) => {
            const exists = prev.gradoIds.includes(cleanId);

            return {
                ...prev,
                gradoIds: exists
                    ? prev.gradoIds.filter((id) => id !== cleanId)
                    : [...prev.gradoIds, cleanId],
            };
        });
    };

    const toggleAllGrades = () => {
        setForm((prev) => ({
            ...prev,
            gradoIds: allGradesSelected
                ? []
                : gradosDisponibles.map((grado) => Number(grado.Grado_ID)),
        }));
    };

    const validateStep = () => {
        if (step === 1) {
            if (!form.regionId) {
                Swal.fire({
                    icon: "warning",
                    title: "Selecciona una región",
                    text: "Primero selecciona México o Latinoamérica.",
                });
                return false;
            }

            if (!form.desafioId) {
                Swal.fire({
                    icon: "warning",
                    title: "Selecciona un desafío",
                    text: "Después de seleccionar la región, selecciona el desafío.",
                });
                return false;
            }
        }

        if (step === 2) {
            if (!form.distyIds.length) {
                Swal.fire({
                    icon: "warning",
                    title: "Selecciona distys",
                    text: "Debes seleccionar al menos un disty.",
                });
                return false;
            }
        }

        if (step === 3) {
            if (!form.gradoIds.length) {
                Swal.fire({
                    icon: "warning",
                    title: "Selecciona grados",
                    text: "Debes seleccionar al menos un grado permitido.",
                });
                return false;
            }

            if (form.fechaInicio && form.fechaFin && form.fechaFin < form.fechaInicio) {
                Swal.fire({
                    icon: "warning",
                    title: "Fechas inválidas",
                    text: "La fecha fin no puede ser menor que la fecha inicio.",
                });
                return false;
            }
        }

        return true;
    };

    const goNext = () => {
        if (!validateStep()) return;
        setStep((prev) => Math.min(prev + 1, 3));
    };

    const goBack = () => {
        setStep((prev) => Math.max(prev - 1, 1));
    };

    const handleSave = async () => {
        if (!validateStep()) return;

        const confirm = await Swal.fire({
            icon: "info",
            title: "¿Guardar asignación?",
            text: "Se asignará el desafío a los distys y grados seleccionados.",
            showCancelButton: true,
            confirmButtonText: "Sí, guardar",
            cancelButtonText: "Cancelar",
        });

        if (!confirm.isConfirmed) return;

        try {
            setSaving(true);

            const responses = [];

            const gradoIdsOrdenados = [...form.gradoIds]
                .map(Number)
                .sort((a, b) => a - b);

            const distyIdsOrdenados = [...form.distyIds]
                .map(Number)
                .sort((a, b) => a - b);

            for (const gradoId of gradoIdsOrdenados) {
                const body = {
                    regionId: Number(form.regionId),
                    gradoId: Number(gradoId),
                    distyIds: distyIdsOrdenados,
                    fechaInicio: toMysqlDateTime(form.fechaInicio),
                    fechaFin: toMysqlDateTime(form.fechaFin),
                    activo: Number(form.activo),
                };

                const res = await fetch(
                    `${API_BASE}/api/admin/champions/desafios/${form.desafioId}/asignaciones/sync`,
                    {
                        method: "PUT",
                        credentials: "include",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(body),
                    }
                );

                const data = await res.json();

                if (!res.ok || data.success === false) {
                    throw new Error(data.message || "No se pudo guardar la asignación.");
                }

                responses.push(data);
            }

            // const responses = await Promise.all(requests);

            const resumen = responses.reduce(
                (acc, item) => {
                    const row = item.data || {};

                    acc.insertados += Number(row.insertados || 0);
                    acc.reactivados += Number(row.reactivados || 0);
                    acc.actualizados += Number(row.actualizados || 0);
                    acc.desactivados += Number(row.desactivados || 0);

                    return acc;
                },
                {
                    insertados: 0,
                    reactivados: 0,
                    actualizados: 0,
                    desactivados: 0,
                }
            );

            await Swal.fire({
                icon: "success",
                title: "Asignación guardada",
                // html: `
                //     <div class="text-start">
                //         <p class="mb-1"><strong>Insertados:</strong> ${resumen.insertados}</p>
                //         <p class="mb-1"><strong>Reactivados:</strong> ${resumen.reactivados}</p>
                //         <p class="mb-1"><strong>Actualizados:</strong> ${resumen.actualizados}</p>
                //         <p class="mb-0"><strong>Desactivados:</strong> ${resumen.desactivados}</p>
                //     </div>
                // `,
            });

            closeModal();

            setDesafioFiltro(form.desafioId);
            setEstadoFiltro("");
            fetchAsignaciones(form.desafioId, "");
        } catch (error) {
            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message || "No se pudo guardar la asignación.",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleStatusAsignacion = async (asignacionId, activo) => {
        const confirm = await Swal.fire({
            icon: "question",
            title: activo === 1 ? "¿Activar asignación?" : "¿Desactivar asignación?",
            text:
                activo === 1
                    ? "La asignación volverá a estar activa."
                    : "La asignación quedará inactiva, no se eliminará.",
            showCancelButton: true,
            confirmButtonText: activo === 1 ? "Sí, activar" : "Sí, desactivar",
            cancelButtonText: "Cancelar",
        });

        if (!confirm.isConfirmed) return;

        try {
            const res = await fetch(
                `${API_BASE}/api/admin/champions/asignaciones/${asignacionId}/status`,
                {
                    method: "PATCH",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ activo }),
                }
            );

            const data = await res.json();

            if (!res.ok || data.success === false) {
                throw new Error(data.message || "No se pudo actualizar la asignación.");
            }

            Swal.fire({
                icon: "success",
                title: "Actualizado",
                text: "La asignación se actualizó correctamente.",
                timer: 1400,
                showConfirmButton: false,
            });

            fetchAsignaciones();
        } catch (error) {
            console.error(error);

            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.message || "No se pudo actualizar la asignación.",
            });
        }
    };

    const cleanFilters = () => {
        setDesafioFiltro("");
        setEstadoFiltro("");
        setAsignaciones([]);
    };

    const openEditModal = async (item) => {
    const regionId = Number(item.Region_ID);
    const desafioId = Number(item.Desafio_ID);
    const gradoId = Number(item.Grado_ID);

    setStep(1);

    setForm({
        regionId,
        desafioId,
        gradoIds: [gradoId],
        distyIds: [],
        fechaInicio: toDatetimeLocalValue(item.Asignacion_Fecha_Inicio),
        fechaFin: toDatetimeLocalValue(item.Asignacion_Fecha_Fin),
        activo: Number(item.Asignacion_Activo) === 1 ? 1 : 0,
    });

    await fetchDistysWithEstado({
        desafioId,
        regionId,
        gradoId,
        fechaInicio: item.Asignacion_Fecha_Inicio,
        fechaFin: item.Asignacion_Fecha_Fin,
        activo: item.Asignacion_Activo,
    });

    const modalEl = document.getElementById("addAsigDesafioModal");

    if (window.bootstrap?.Modal && modalEl) {
        const modal =
            window.bootstrap.Modal.getInstance(modalEl) ||
            new window.bootstrap.Modal(modalEl);

        modal.show();
    }
};

const fetchDistysWithEstado = async ({
    desafioId,
    regionId,
    gradoId,
}) => {
    try {
        setLoadingDistys(true);

        const params = new URLSearchParams();
        params.set("regionId", regionId);
        params.set("gradoId", gradoId);

        const res = await fetch(
            `${API_BASE}/api/admin/champions/desafios/${desafioId}/asignaciones/distys?${params.toString()}`,
            {
                credentials: "include",
            }
        );

        const data = await res.json();

        if (!res.ok || data.success === false) {
            throw new Error(data.message || "No se pudieron cargar los distys asignados.");
        }

        const items = parseItems(data);

        setDistys(items);

        setForm((prev) => ({
            ...prev,
            distyIds: items
                .filter((disty) => Boolean(disty.asignado))
                .map((disty) => Number(disty.Disty_ID)),
        }));
    } catch (error) {
        console.error(error);

        Swal.fire({
            icon: "error",
            title: "Error",
            text: error.message || "No se pudieron cargar los distys asignados.",
        });
    } finally {
        setLoadingDistys(false);
    }
};

    return (
        <>
            <Sidebar />

            <main className="main-content">
                <Header />

                <div className="content-inner container-fluid pb-0" id="page_layout">
                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3">
                        <div className="d-flex flex-column">
                            <h3>Asignación Champions</h3>
                            <p className="text-primary mb-0">Panduit Core</p>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-end">
                                    <div className="row g-2 align-items-end w-100">
                                        <div className="col-md-5">
                                            <label className="form-label mb-1">Desafío</label>
                                            <select
                                                className="form-select form-select-sm"
                                                value={desafioFiltro}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setDesafioFiltro(value);
                                                    fetchAsignaciones(value, estadoFiltro);
                                                }}
                                            >
                                                <option value="">
                                                    Selecciona un desafío para ver asignaciones
                                                </option>

                                                {desafios.map((desafio) => (
                                                    <option
                                                        key={desafio.Desafio_ID}
                                                        value={desafio.Desafio_ID}
                                                    >
                                                        {desafio.Desafio_Titulo}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="col-md-2">
                                            <label className="form-label mb-1">Estado</label>
                                            <select
                                                className="form-select form-select-sm"
                                                value={estadoFiltro}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    setEstadoFiltro(value);
                                                    fetchAsignaciones(desafioFiltro, value);
                                                }}
                                                disabled={!desafioFiltro}
                                            >
                                                <option value="">Todos</option>
                                                <option value="1">Activos</option>
                                                <option value="0">Inactivos</option>
                                            </select>
                                        </div>

                                        <div className="col-md-5 d-flex justify-content-end gap-2 ms-auto">
                                            <button
                                                type="button"
                                                className="btn btn-secondary-subtle btn-sm"
                                                title="Limpiar filtros"
                                                onClick={cleanFilters}
                                            >
                                                <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <g opacity="0.4">
                                                    <path
                                                        d="M4.88076 14.6713C4.74978 14.2784 4.32504 14.066 3.93208 14.197C3.53912 14.328 3.32675 14.7527 3.45774 15.1457L4.88076 14.6713ZM20.8808 15.1457C21.0117 14.7527 20.7994 14.328 20.4064 14.197C20.0135 14.066 19.5887 14.2784 19.4577 14.6713L20.8808 15.1457ZM4.16925 14.9085C3.45774 15.1457 3.45785 15.146 3.45797 15.1464C3.45802 15.1465 3.45815 15.1469 3.45825 15.1472C3.45845 15.1478 3.45868 15.1485 3.45895 15.1493C3.45948 15.1509 3.46013 15.1528 3.46092 15.1551C3.46249 15.1597 3.46456 15.1657 3.46716 15.1731C3.47235 15.188 3.47961 15.2084 3.48902 15.2341C3.50782 15.2854 3.53521 15.3576 3.5717 15.4477C3.64461 15.6279 3.7542 15.8805 3.90468 16.1814C4.2048 16.7817 4.67223 17.5836 5.34308 18.3886C6.68942 20.0043 8.88343 21.6585 12.1693 21.6585V20.1585C9.45507 20.1585 7.64908 18.8128 6.49542 17.4284C5.91627 16.7334 5.5087 16.0354 5.24632 15.5106C5.11555 15.2491 5.02201 15.0329 4.96212 14.8849C4.9322 14.811 4.91076 14.7543 4.89733 14.7177C4.89062 14.6994 4.88593 14.6861 4.88318 14.6783C4.88181 14.6744 4.88093 14.6718 4.88053 14.6706C4.88033 14.67 4.88025 14.6698 4.88029 14.6699C4.88031 14.67 4.88036 14.6701 4.88044 14.6704C4.88047 14.6705 4.88056 14.6707 4.88058 14.6708C4.88067 14.671 4.88076 14.6713 4.16925 14.9085ZM12.1693 21.6585C15.4551 21.6585 17.6491 20.0043 18.9954 18.3886C19.6663 17.5836 20.1337 16.7817 20.4338 16.1814C20.5843 15.8805 20.6939 15.6279 20.7668 15.4477C20.8033 15.3576 20.8307 15.2854 20.8495 15.2341C20.8589 15.2084 20.8662 15.188 20.8713 15.1731C20.8739 15.1657 20.876 15.1597 20.8776 15.1551C20.8784 15.1528 20.879 15.1509 20.8796 15.1493C20.8798 15.1485 20.8801 15.1478 20.8803 15.1472C20.8804 15.1469 20.8805 15.1465 20.8805 15.1464C20.8807 15.146 20.8808 15.1457 20.1693 14.9085C19.4577 14.6713 19.4578 14.671 19.4579 14.6708C19.4579 14.6707 19.458 14.6705 19.4581 14.6704C19.4581 14.6701 19.4582 14.67 19.4582 14.6699C19.4583 14.6698 19.4582 14.67 19.458 14.6706C19.4576 14.6718 19.4567 14.6744 19.4553 14.6783C19.4526 14.6861 19.4479 14.6994 19.4412 14.7177C19.4277 14.7543 19.4063 14.811 19.3764 14.8849C19.3165 15.0329 19.223 15.2491 19.0922 15.5106C18.8298 16.0354 18.4222 16.7334 17.8431 17.4284C16.6894 18.8128 14.8834 20.1585 12.1693 20.1585V21.6585Z"
                                                        fill="currentColor"></path>
                                                    <path d="M21.5183 19.2271C21.4293 19.2234 21.3427 19.196 21.2671 19.1465L16.3546 15.8924C16.2197 15.8026 16.1413 15.6537 16.148 15.4969C16.1546 15.34 16.2452 15.1982 16.3873 15.1202L21.5571 12.2926C21.7075 12.2106 21.8932 12.213 22.0416 12.3003C22.1907 12.387 22.2783 12.5436 22.2712 12.7096L22.014 18.7913C22.007 18.9573 21.9065 19.1059 21.7506 19.1797C21.6772 19.215 21.597 19.2305 21.5183 19.2271" fill="currentColor"></path>
                                                    </g>
                                                    <path
                                                    d="M20.0742 10.0265C20.1886 10.4246 20.6041 10.6546 21.0022 10.5401C21.4003 10.4257 21.6302 10.0102 21.5158 9.61214L20.0742 10.0265ZM4.10803 8.88317C3.96071 9.27031 4.15513 9.70356 4.54226 9.85087C4.92939 9.99818 5.36265 9.80377 5.50996 9.41664L4.10803 8.88317ZM20.795 9.81934C21.5158 9.61214 21.5157 9.6118 21.5156 9.61144C21.5155 9.61129 21.5154 9.6109 21.5153 9.61059C21.5152 9.60998 21.515 9.60928 21.5147 9.60848C21.5143 9.60689 21.5137 9.60493 21.513 9.6026C21.5116 9.59795 21.5098 9.59184 21.5075 9.58431C21.503 9.56925 21.4966 9.54853 21.4882 9.52251C21.4716 9.47048 21.4473 9.39719 21.4146 9.3056C21.3493 9.12256 21.2503 8.8656 21.1126 8.55861C20.8378 7.94634 20.4044 7.12552 19.7678 6.29313C18.4902 4.62261 16.3673 2.87801 13.0844 2.74053L13.0216 4.23922C15.7334 4.35278 17.4816 5.77291 18.5763 7.20436C19.1258 7.92295 19.5038 8.63743 19.744 9.17271C19.8638 9.43949 19.9482 9.65937 20.0018 9.80972C20.0286 9.88483 20.0477 9.94238 20.0596 9.97951C20.0655 9.99808 20.0696 10.0115 20.072 10.0195C20.0732 10.0235 20.074 10.0261 20.0744 10.0273C20.0746 10.0278 20.0746 10.0281 20.0746 10.028C20.0746 10.0279 20.0745 10.0278 20.0745 10.0275C20.0744 10.0274 20.0744 10.0272 20.0743 10.0271C20.0743 10.0268 20.0742 10.0265 20.795 9.81934ZM13.0844 2.74053C9.80146 2.60306 7.54016 4.16407 6.12741 5.72193C5.42345 6.49818 4.92288 7.27989 4.59791 7.86704C4.43497 8.16144 4.31491 8.40923 4.23452 8.58617C4.1943 8.67471 4.16391 8.7457 4.14298 8.79616C4.13251 8.82139 4.1244 8.84151 4.11859 8.85613C4.11568 8.86344 4.11336 8.86938 4.1116 8.8739C4.11072 8.87616 4.10998 8.87807 4.10939 8.87962C4.10909 8.88039 4.10883 8.88108 4.1086 8.88167C4.10849 8.88196 4.10834 8.88234 4.10829 8.88249C4.10815 8.88284 4.10803 8.88317 4.80899 9.14991C5.50996 9.41664 5.50985 9.41692 5.50975 9.41719C5.50973 9.41725 5.50964 9.41749 5.50959 9.4176C5.5095 9.41784 5.50945 9.41798 5.50942 9.41804C5.50938 9.41816 5.50947 9.41792 5.50969 9.41734C5.51014 9.41619 5.51113 9.41365 5.51267 9.40979C5.51574 9.40206 5.52099 9.38901 5.52846 9.37101C5.5434 9.335 5.56719 9.27924 5.60018 9.20664C5.66621 9.0613 5.76871 8.84925 5.91031 8.59341C6.19442 8.08008 6.63084 7.39971 7.23855 6.72958C8.44912 5.39466 10.3098 4.12566 13.0216 4.23922L13.0844 2.74053Z"
                                                    fill="currentColor"></path>
                                                    <path d="M8.78337 9.33604C8.72981 9.40713 8.65805 9.46292 8.57443 9.49703L3.1072 11.6951C2.95672 11.7552 2.78966 11.7352 2.66427 11.6407C2.53887 11.5462 2.47359 11.3912 2.48993 11.2299L3.09576 5.36863C3.11367 5.19823 3.22102 5.04666 3.37711 4.97402C3.5331 4.9005 3.71173 4.91728 3.84442 5.01726L8.70581 8.68052C8.8385 8.78051 8.90387 8.94759 8.8762 9.1178C8.86358 9.19825 8.83082 9.27308 8.78337 9.33604" fill="currentColor"></path>
                                                </svg>
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                onClick={openModal}
                                            >
                                                Asignar Desafío
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="card-body px-0">
                                    <div className="table-responsive">
                                        <table
                                            id="user-list-table"
                                            className="table table-striped table-hover mb-0"
                                            role="grid"
                                            data-toggle="data-table"
                                        >
                                            <thead>
                                                <tr className="ligth">
                                                    <th>Desafío</th>
                                                    <th>Región</th>
                                                    <th>Grado</th>
                                                    <th>Disty</th>
                                                    <th>Vigencia</th>
                                                    <th>Estado</th>
                                                    <th>Acción</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {!desafioFiltro ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4 text-muted">
                                                            Selecciona un desafío para consultar sus asignaciones.
                                                        </td>
                                                    </tr>
                                                ) : loadingAsignaciones ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center">
                                                            <div className="d-flex flex-column align-items-center justify-content-center gap-2 py-4">
                                                                <div className="col-md-12 col-sm-4 mb-4">
                                                                    <div className="iq-loader-box">
                                                                        <div className="iq-loader-1"></div>
                                                                    </div>
                                                                </div>

                                                                <span className="text-muted">
                                                                    Cargando asignaciones...
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : asignaciones.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4 text-muted">
                                                            Este desafío todavía no tiene asignaciones.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    asignaciones.map((item) => (
                                                        <tr key={item.Asignacion_ID}>
                                                            <td>
                                                                <strong>{item.Desafio_Titulo}</strong>
                                                                {/* <div className="small text-muted">
                                                                    ID: {item.Desafio_ID}
                                                                </div> */}
                                                            </td>

                                                            <td>{item.Region_Nombre}</td>

                                                            <td>{item.Grado_Nombre}</td>

                                                            <td>{item.Disty_Nombre}</td>

                                                            <td>
                                                                <div className="small">
                                                                    <strong>Inicio:</strong>{" "}
                                                                    {formatDateTimeMx(item.Asignacion_Fecha_Inicio)}
                                                                </div>
                                                                <div className="small">
                                                                    <strong>Fin:</strong>{" "}
                                                                    {formatDateTimeMx(item.Asignacion_Fecha_Fin)}
                                                                </div>
                                                            </td>

                                                            <td>
                                                                {Number(item.Asignacion_Activo) === 1 ? (
                                                                    <span className="badge bg-success">
                                                                        Activo
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-secondary">
                                                                        Inactivo
                                                                    </span>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <div className="d-flex gap-2 flex-wrap">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-primary"
                                                                        onClick={() => openEditModal(item)}
                                                                    >
                                                                        Editar
                                                                    </button>

                                                                    {Number(item.Asignacion_Activo) === 1 ? (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-danger"
                                                                            onClick={() =>
                                                                                handleStatusAsignacion(item.Asignacion_ID, 0)
                                                                            }
                                                                        >
                                                                            Desactivar
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-success"
                                                                            onClick={() =>
                                                                                handleStatusAsignacion(item.Asignacion_ID, 1)
                                                                            }
                                                                        >
                                                                            Activar
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <div
                className="modal fade"
                id="addAsigDesafioModal"
                tabIndex="-1"
                aria-labelledby="addAsigDesafioModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-dialog-centered modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <div>
                                <h5 className="modal-title" id="addAsigDesafioModalLabel">
                                    Asignar Desafío Champions
                                </h5>
                                <p className="small text-muted mb-0">
                                    Primero selecciona región, después desafío, distys y grados.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={resetForm}
                            ></button>
                        </div>

                        <div className="modal-body">
                            <div className="row mb-4">
                                <div className="col-md-4">
                                    <div
                                        className={`p-3 rounded border ${
                                            step === 1 ? "border-primary bg-primary-subtle" : ""
                                        }`}
                                    >
                                        <strong>1. Región y desafío</strong>
                                        <div className="small text-muted">
                                            Selección inicial
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div
                                        className={`p-3 rounded border ${
                                            step === 2 ? "border-primary bg-primary-subtle" : ""
                                        }`}
                                    >
                                        <strong>2. Distys</strong>
                                        <div className="small text-muted">
                                            Distribuidores con acceso
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div
                                        className={`p-3 rounded border ${
                                            step === 3 ? "border-primary bg-primary-subtle" : ""
                                        }`}
                                    >
                                        <strong>3. Grados y vigencia</strong>
                                        <div className="small text-muted">
                                            Niveles permitidos
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {step === 1 && (
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Región</label>
                                        <select
                                            className="form-select"
                                            value={form.regionId}
                                            onChange={(e) => handleRegionChange(e.target.value)}
                                        >
                                            <option value="">Selecciona región</option>

                                            {REGIONES_CHAMPIONS.map((region) => (
                                                <option
                                                    key={region.Region_ID}
                                                    value={region.Region_ID}
                                                >
                                                    {region.Region_Nombre}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Desafío</label>
                                        <select
                                            className="form-select"
                                            value={form.desafioId}
                                            disabled={!form.regionId || loadingDesafios}
                                            onChange={(e) =>
                                                setForm((prev) => ({
                                                    ...prev,
                                                    desafioId: e.target.value
                                                        ? Number(e.target.value)
                                                        : "",
                                                }))
                                            }
                                        >
                                            <option value="">
                                                {!form.regionId
                                                    ? "Primero selecciona una región"
                                                    : "Selecciona un desafío"}
                                            </option>

                                            {desafios.map((desafio) => (
                                                <option
                                                    key={desafio.Desafio_ID}
                                                    value={desafio.Desafio_ID}
                                                >
                                                    {desafio.Desafio_Titulo}
                                                </option>
                                            ))}
                                        </select>

                                        {form.regionId && desafios.length === 0 && !loadingDesafios && (
                                            <div className="small text-danger mt-1">
                                                No se encontraron desafíos. Revisa la ruta GET /api/admin/champions/desafios.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {step === 2 && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <div className="d-flex justify-content-between align-items-end flex-wrap gap-2">
                                            <div>
                                                <label className="form-label">Distys</label>
                                                <p className="small text-muted mb-0">
                                                    Seleccionados: {form.distyIds.length} de {distys.length}
                                                </p>
                                            </div>

                                            <div className="d-flex gap-2">
                                                <input
                                                    type="text"
                                                    className="form-control form-control-sm"
                                                    placeholder="Buscar disty"
                                                    value={searchDisty}
                                                    onChange={(e) => setSearchDisty(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") {
                                                            fetchDistysByRegion(form.regionId, searchDisty);
                                                        }
                                                    }}
                                                />

                                                <button
                                                    type="button"
                                                    className="btn btn-outline-primary btn-sm"
                                                    disabled={loadingDistys}
                                                    onClick={() =>
                                                        fetchDistysByRegion(form.regionId, searchDisty)
                                                    }
                                                >
                                                    Buscar
                                                </button>
                                            </div>
                                        </div>

                                        <div className="border rounded mt-2">
                                            <div className="p-3 border-bottom bg-light">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="checkAllDistys"
                                                        checked={allDistysSelected}
                                                        onChange={toggleAllDistys}
                                                        disabled={!distys.length || loadingDistys}
                                                    />

                                                    <label
                                                        className="form-check-label fw-bold"
                                                        htmlFor="checkAllDistys"
                                                    >
                                                        Seleccionar todos los distys
                                                    </label>
                                                </div>
                                            </div>

                                            <div>
                                                {loadingDistys ? (
                                                    <div className="text-center py-4 text-muted">
                                                        Cargando distys...
                                                    </div>
                                                ) : distys.length === 0 ? (
                                                    <div className="text-center py-4 text-muted">
                                                        No hay distys para esta región.
                                                    </div>
                                                ) : (
                                                    <div className="row g-0">
                                                        {distys.map((disty) => (
                                                            <div
                                                                className="col-md-4 border-bottom"
                                                                key={disty.Disty_ID}
                                                            >
                                                                <div className="form-check p-3 ps-5">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="checkbox"
                                                                        id={`disty-${disty.Disty_ID}`}
                                                                        checked={form.distyIds.includes(
                                                                            Number(disty.Disty_ID)
                                                                        )}
                                                                        onChange={() =>
                                                                            toggleDisty(disty.Disty_ID)
                                                                        }
                                                                    />

                                                                    <label
                                                                        className="form-check-label"
                                                                        htmlFor={`disty-${disty.Disty_ID}`}
                                                                    >
                                                                        <strong>
                                                                            {disty.Disty_Nombre}
                                                                        </strong>

                                                                        <div className="small text-muted">
                                                                            {disty.Region_Nombre || "Sin región"}
                                                                        </div>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {step === 3 && (
                                <div className="row g-3">
                                    <div className="col-12">
                                        <div className="alert alert-info mb-0">
                                            {Number(form.regionId) === 1 ? (
                                                <>
                                                    Para <strong>México</strong> están permitidos:
                                                    Especialista, Elite y Product Manager.
                                                </>
                                            ) : (
                                                <>
                                                    Para <strong>Latinoamérica</strong> solo está permitido:
                                                    Elite y Lider.
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <div className="form-check mb-3">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="checkAllGrades"
                                                checked={allGradesSelected}
                                                onChange={toggleAllGrades}
                                                disabled={!gradosDisponibles.length}
                                            />

                                            <label
                                                className="form-check-label fw-bold"
                                                htmlFor="checkAllGrades"
                                            >
                                                Seleccionar todos los grados permitidos
                                            </label>
                                        </div>

                                        <div className="row g-3">
                                            {gradosDisponibles.map((grado) => (
                                                <div className="col-md-4" key={grado.Grado_ID}>
                                                    <div className="border rounded p-3 h-100">
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`grado-${grado.Grado_ID}`}
                                                                checked={form.gradoIds.includes(
                                                                    Number(grado.Grado_ID)
                                                                )}
                                                                onChange={() => toggleGrade(grado.Grado_ID)}
                                                            />

                                                            <label
                                                                className="form-check-label fw-bold"
                                                                htmlFor={`grado-${grado.Grado_ID}`}
                                                            >
                                                                {grado.Grado_Nombre}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">Fecha inicio</label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            value={form.fechaInicio}
                                            onChange={(e) =>
                                                setForm((prev) => ({
                                                    ...prev,
                                                    fechaInicio: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">Fecha fin</label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            value={form.fechaFin}
                                            onChange={(e) =>
                                                setForm((prev) => ({
                                                    ...prev,
                                                    fechaFin: e.target.value,
                                                }))
                                            }
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">Estado</label>
                                        <select
                                            className="form-select"
                                            value={form.activo}
                                            onChange={(e) =>
                                                setForm((prev) => ({
                                                    ...prev,
                                                    activo: Number(e.target.value),
                                                }))
                                            }
                                        >
                                            <option value={1}>Activo</option>
                                            <option value={0}>Inactivo</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                                onClick={resetForm}
                                disabled={saving}
                            >
                                Cancelar
                            </button>

                            {step > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={goBack}
                                    disabled={saving}
                                >
                                    Anterior
                                </button>
                            )}

                            {step < 3 ? (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={goNext}
                                    disabled={saving}
                                >
                                    Siguiente
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? "Guardando..." : "Guardar asignación"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ChampionsAsignacion;